import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Loader2, X, Download } from 'lucide-react';
import { api } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';

/**
 * Who is working the queue — per person, over a date range.
 *
 * Two columns that mean different things, kept apart on purpose:
 *   • **Closed** is counted on the day the ticket was closed and credited to whoever closed it, so
 *     it answers "what did this person get through in this period".
 *   • **Open** is the person's CURRENT backlog and carries no date — a ticket still open is open
 *     whatever range is selected. Dating it would make the two columns look comparable when they
 *     are not, which is the usual way a support report ends up being quietly wrong.
 *
 * The figures are scoped exactly as the ticket list is, so a manager sees their team and an
 * executive sees their own desk.
 */
export function TicketSummaryDialog({ open, onClose, source, ageing }: {
  open: boolean; onClose: () => void;
  /** Merchant Tickets or Internal Tickets — the tab the page is on. Each has its own summary. */
  source?: 'MERCHANT' | 'INTERNAL';
  ageing?: string;
}) {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [from, setFrom] = useState(ymd(first));
  const [to, setTo] = useState(ymd(today));
  // One person or several; empty = everyone. 'none' = Unassigned.
  const [people, setPeople] = useState<(string | number)[]>([]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['ticket-summary', from, to, source, ageing, people.join(',')],
    queryFn: async () => (await api.get('/tickets/summary', { params: { from, to, source, ageing: ageing || undefined, userIds: people.length ? people.join(',') : undefined } })).data as any,
    enabled: open,
    // Keep the last figures on screen while a new filter loads, so the table does not blink away.
    placeholderData: (prev) => prev,
  });

  // The picker lists everyone tickets can be assigned to (same cache as the ticket page), plus anyone
  // who has appeared in a summary — someone who closed tickets and has since left the desk is still
  // in the report, so must still be pickable. Remembered across filters so picking one person does
  // not shrink the list to that person.
  const { data: assignable } = useQuery<{ items: { id: number; name: string; designation?: string | null; userType?: string }[] }>({
    queryKey: ['ticket-assignable'], enabled: open,
    queryFn: async () => (await api.get('/tickets/assignable')).data,
  });
  const seen = useRef(new Map<number, string>());
  useEffect(() => { for (const u of data?.users ?? []) if (u.userId) seen.current.set(u.userId, u.name); }, [data]);
  const peopleOptions = useMemo(() => {
    const out = [{ value: 'none' as string | number, label: 'Unassigned', hint: '' }];
    const ids = new Set<number>();
    for (const u of assignable?.items ?? []) { ids.add(u.id); out.push({ value: u.id, label: u.name, hint: u.designation ?? u.userType?.replace(/_/g, ' ') ?? '' }); }
    for (const [id, name] of seen.current) if (!ids.has(id)) out.push({ value: id, label: name, hint: '' });
    return out;
  }, [assignable, data]);
  const pickedNames = people.map((v) => peopleOptions.find((o) => o.value === v)?.label ?? String(v));

  const preset = (days: number) => {
    const end = new Date();
    const start = new Date(end.getTime() - (days - 1) * 86400_000);
    setFrom(ymd(start)); setTo(ymd(end));
  };
  const thisMonth = () => { setFrom(ymd(new Date(today.getFullYear(), today.getMonth(), 1))); setTo(ymd(today)); };

  /** Straight from what is on screen — no second endpoint to drift from it. */
  const exportCsv = () => {
    const rows = [['Person', 'All Tickets', 'Closed in range', 'Open now', 'Pending Tickets', 'Closure TAT', 'Closure TAT (hours)']];
    const line = (name: string, u: any) => [name, String(u?.all ?? 0), String(u?.closed ?? 0), String(u?.open ?? 0), String(u?.pending ?? 0), fmtTat(u?.closureTatHours), u?.closureTatHours == null ? '' : String(u.closureTatHours)];
    for (const u of data?.users ?? []) rows.push(line(u.name, u));
    rows.push(line('TOTAL', data?.totals));
    const csv = rows.map((r) => r.map((c) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    const who = people.length === 1 ? `-${pickedNames[0].replace(/[^A-Za-z0-9]+/g, '_')}` : people.length > 1 ? `-${people.length}-people` : '';
    a.href = url; a.download = `ticket-summary-${from}-to-${to}${who}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    // Revoked late: revoking in the same tick cancels the save in Chrome.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><BarChart3 size={17} /> {source === 'INTERNAL' ? 'Internal tickets' : source === 'MERCHANT' ? 'Merchant tickets' : 'Ticket'} summary</h3>
            <p className="text-xs text-slate-500">Who closed what, and who is carrying what</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">From</label>
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="input py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">To</label>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="input py-1.5 text-sm" />
          </div>
          <div className="flex gap-1">
            <button onClick={() => preset(7)} className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs hover:bg-slate-50">7 days</button>
            <button onClick={() => preset(30)} className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs hover:bg-slate-50">30 days</button>
            <button onClick={thisMonth} className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs hover:bg-slate-50">This month</button>
          </div>
          <MultiSelectFilter label="People" options={peopleOptions} values={people} onChange={setPeople}
            placeholder="Everyone" className="w-60" />
          <button onClick={exportCsv} disabled={!data?.users?.length}
            className="ml-auto px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs flex items-center gap-1 hover:bg-slate-50 disabled:opacity-50">
            <Download size={12} /> Export
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 px-5 py-4">
              <Tile label="Raised in range" value={data?.totals?.raised} tone="text-slate-900" hint={people.length ? 'Assigned to the selected' : undefined} />
              <Tile label="Closed in range" value={data?.totals?.closed} tone="text-emerald-700" />
              <Tile label="Open right now" value={data?.totals?.open} tone="text-amber-700" hint="Not date-filtered" />
            </div>

            <div className="px-5 pb-4">
              <div className="text-xs font-medium text-slate-600 mb-1.5">
                By person{people.length ? ` — ${people.length === 1 ? pickedNames[0] : `${people.length} selected`}` : ''} {isFetching && <Loader2 size={11} className="inline animate-spin" />}
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[40vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-xs text-slate-600">
                      <th className="px-3 py-2">Person</th>
                      <th className="px-3 py-2 text-right" title="Raised in the range and assigned to them now, any status">All Tickets</th>
                      <th className="px-3 py-2 text-right">Closed in range</th>
                      <th className="px-3 py-2 text-right">Open now</th>
                      <th className="px-3 py-2 text-right" title="Of All Tickets, the ones not closed yet">Pending Tickets</th>
                      <th className="px-3 py-2 text-right" title="Average time from raising to closing, for the tickets they closed in the range">Closure TAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.users ?? []).map((u: any) => (
                      <tr key={String(u.userId ?? 'none')} className="border-t border-slate-100">
                        <td className="px-3 py-2">
                          <span className={u.userId ? 'text-slate-800' : 'text-slate-500 italic'}>{u.name}</span>
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-slate-800">{u.all || '—'}</td>
                        <td className="px-3 py-2 text-right font-medium text-emerald-700">{u.closed || '—'}</td>
                        <td className="px-3 py-2 text-right font-medium text-amber-700">{u.open || '—'}</td>
                        <td className="px-3 py-2 text-right font-medium text-rose-700">{u.pending || '—'}</td>
                        <td className="px-3 py-2 text-right text-slate-700">{fmtTat(u.closureTatHours) || '—'}</td>
                      </tr>
                    ))}
                    {!!(data?.users ?? []).length && (
                      <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold" data-testid="tsum-total">
                        <td className="px-3 py-2">TOTAL</td>
                        <td className="px-3 py-2 text-right">{data?.totals?.all ?? 0}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{data?.totals?.closed ?? 0}</td>
                        <td className="px-3 py-2 text-right text-amber-700">{data?.totals?.open ?? 0}</td>
                        <td className="px-3 py-2 text-right text-rose-700">{data?.totals?.pending ?? 0}</td>
                        <td className="px-3 py-2 text-right">{fmtTat(data?.totals?.closureTatHours) || '—'}</td>
                      </tr>
                    )}
                    {!(data?.users ?? []).length && (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No tickets closed or open in this range.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">
                All Tickets = raised in the range and assigned to them; Pending = of those, not closed yet. Closed is counted on the day it was closed, by whoever closed it, and Closure TAT is the average time from raising to closing for those. Open is each person's backlog right now, so it does not change with the dates.
              </p>
            </div>

            {!!(data?.byCategory ?? []).length && (
              <div className="px-5 pb-5 grid sm:grid-cols-2 gap-4">
                <Breakdown title="Raised by category" rows={(data?.byCategory ?? []).map((c: any) => ({ label: c.category.replace(/_/g, ' '), n: c.count }))} />
                <Breakdown title="Raised by priority" rows={(data?.byPriority ?? []).map((c: any) => ({ label: c.priority, n: c.count }))} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Average closure time, read the way people say it: 45m, 6.5h, 2d 4h. */
function fmtTat(h?: number | null): string {
  if (h == null) return '';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 24) return `${Math.round(h * 10) / 10}h`;
  const d = Math.floor(h / 24);
  const rest = Math.round(h - d * 24);
  return rest ? `${d}d ${rest}h` : `${d}d`;
}

function Tile({ label, value, tone, hint }: { label: string; value?: number; tone: string; hint?: string }) {
  return (
    <div className="border border-slate-200 rounded-lg px-3 py-2.5">
      <div className={`text-2xl font-bold ${tone}`}>{value ?? '—'}</div>
      <div className="text-[11px] text-slate-600 leading-tight">{label}</div>
      {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: Array<{ label: string; n: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div>
      <div className="text-xs font-medium text-slate-600 mb-1.5">{title}</div>
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-2 text-xs">
            <div className="w-32 truncate text-slate-600" title={r.label}>{r.label}</div>
            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
              <div className="bg-primary/70 h-full rounded-full" style={{ width: `${(r.n / max) * 100}%` }} />
            </div>
            <div className="w-8 text-right text-slate-700 font-medium">{r.n}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
